import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { Monitor, Sun, Moon, Mail, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const THEMES = [
  { key: "system", label: "System", icon: Monitor },
  { key: "light", label: "Light", icon: Sun },
  { key: "dark", label: "Dark", icon: Moon },
];

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-brand-teal" : "bg-muted"}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

export default function PreferencesSettings() {
  const { theme, setTheme } = useTheme();
  const { user, updatePreferences } = useAuth();
  const prefs = user?.preferences || {};
  const [productUpdates, setProductUpdates] = useState(prefs.product_updates ?? true);
  const [weeklyDigest, setWeeklyDigest] = useState(prefs.weekly_digest ?? false);
  const [dailyBriefing, setDailyBriefing] = useState(prefs.daily_briefing_email ?? false);
  const [sending, setSending] = useState(false);

  const sendBriefingNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/ai/briefing/email");
      if (data.success === false) toast.error(data.message || "Could not send briefing email.");
      else toast.success(data.message || "Briefing sent to your inbox.");
    } catch {
      toast.error("Could not send briefing email.");
    } finally {
      setSending(false);
    }
  };

  const savePref = async (key, value, setter) => {
    setter(value);
    try {
      await updatePreferences({ [key]: value });
      toast.success("Preference saved.");
    } catch {
      setter(!value);
      toast.error("Could not save preference.");
    }
  };

  return (
    <div className="space-y-4" data-testid="preferences-settings">
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
        <h2 className="font-display text-xl font-semibold tracking-tight">Appearance</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose how Workspace looks. Your choice is remembered on this device.</p>
        <div className="mt-5 grid grid-cols-3 gap-3">
          {THEMES.map((t) => {
            const active = theme === t.key;
            const TIcon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTheme(t.key)}
                data-testid={`theme-option-${t.key}`}
                className={`flex flex-col items-center gap-2 rounded-xl border p-4 text-sm font-medium transition-colors ${
                  active ? "border-brand-teal bg-brand-teal/8 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <TIcon className="h-5 w-5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
        <h2 className="font-display text-xl font-semibold tracking-tight">Notifications</h2>
        <p className="mt-1 text-sm text-muted-foreground">Control the emails you receive from Workspace.</p>
        <div className="mt-5 divide-y divide-border">
          <div className="flex items-center justify-between py-4">
            <div>
              <p className="text-sm font-medium">Product updates</p>
              <p className="text-xs text-muted-foreground">News about new modules and features.</p>
            </div>
            <Toggle checked={productUpdates} onChange={(v) => savePref("product_updates", v, setProductUpdates)} testid="pref-product-updates" />
          </div>
          <div className="flex items-center justify-between py-4">
            <div>
              <p className="text-sm font-medium">Weekly digest</p>
              <p className="text-xs text-muted-foreground">A weekly summary once modules are live.</p>
            </div>
            <Toggle checked={weeklyDigest} onChange={(v) => savePref("weekly_digest", v, setWeeklyDigest)} testid="pref-weekly-digest" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg brand-gradient-bg text-white"><Mail className="h-4 w-4" /></span>
          <h2 className="font-display text-xl font-semibold tracking-tight">Daily AI briefing email</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Start each morning with your Business Briefing delivered to your inbox.</p>
        <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
          <div>
            <p className="text-sm font-medium">Email me my briefing every morning</p>
            <p className="text-xs text-muted-foreground">Sent daily at 7:00 AM UTC.</p>
          </div>
          <Toggle checked={dailyBriefing} onChange={(v) => savePref("daily_briefing_email", v, setDailyBriefing)} testid="pref-daily-briefing" />
        </div>
        <button onClick={sendBriefingNow} disabled={sending} data-testid="send-briefing-now-btn" className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60">
          <Send className="h-4 w-4" /> {sending ? "Sending…" : "Email me today's briefing"}
        </button>
      </div>

      <ConsentsCard />
    </div>
  );
}

function ConsentsCard() {
  const { user, checkAuth } = useAuth();
  const consents = user?.consents || {};
  const [marketing, setMarketing] = useState(!!consents.marketing?.accepted);
  const [showcase, setShowcase] = useState(!!consents.showcase?.accepted);

  useEffect(() => {
    setMarketing(!!consents.marketing?.accepted);
    setShowcase(!!consents.showcase?.accepted);
  }, [consents.marketing?.accepted, consents.showcase?.accepted]);

  const save = async (key, value, setter) => {
    setter(value);
    try {
      await api.patch("/auth/consents", { [key]: value });
      await checkAuth();
      toast.success(value ? "Consent granted." : "Consent withdrawn.");
    } catch {
      setter(!value);
      toast.error("Could not update consent.");
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="consents-card">
      <h2 className="font-display text-xl font-semibold tracking-tight">Consents & communications</h2>
      <p className="mt-1 text-sm text-muted-foreground">Withdraw or re-grant the optional consents you gave at signup. Terms & Privacy remain in effect while your account is active.</p>
      <div className="mt-5 divide-y divide-border">
        <div className="flex items-center justify-between py-3">
          <div className="pr-6">
            <p className="text-sm font-medium">Product updates & announcements</p>
            <p className="text-xs text-muted-foreground">New features, useful resources and promotional communications from StUpEco.</p>
            {consents.marketing?.at && (
              <p className="mt-0.5 text-[10px] text-muted-foreground">Last changed: {consents.marketing.at.slice(0, 10)}</p>
            )}
          </div>
          <Toggle checked={marketing} onChange={(v) => save("marketing", v, setMarketing)} testid="consent-marketing-toggle" />
        </div>
        <div className="flex items-center justify-between py-3">
          <div className="pr-6">
            <p className="text-sm font-medium">Public showcase</p>
            <p className="text-xs text-muted-foreground">Allow StUpEco to identify your business/profile in promotional, community or showcase materials.</p>
            {consents.showcase?.at && (
              <p className="mt-0.5 text-[10px] text-muted-foreground">Last changed: {consents.showcase.at.slice(0, 10)}</p>
            )}
          </div>
          <Toggle checked={showcase} onChange={(v) => save("showcase", v, setShowcase)} testid="consent-showcase-toggle" />
        </div>
      </div>
    </div>
  );
}
